import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, notification } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';
import './i18n'; // Import i18n configuration

// 导入实际组件
import AppLayout from './components/Layout';
import Dashboard from './pages/Dashboard';
//import DashboardDemo from './pages/DashboardDemo';
import Notes from './pages/Notes';
import NotePage from './pages/NotePage';
import NoteContentPage from './pages/NoteContentPage';
import AIWriterHistory from './pages/AIWriterHistory';
import CreateNote from './pages/CreateNote';
import Search from './pages/Search';
import Logs from './pages/Logs';
import Login from './pages/admin/Login';
import Register from './pages/admin/Register';
import ForgotPassword from './pages/admin/ForgotPassword';
import ResetPassword from './pages/admin/ResetPassword';
import AuthError from './pages/AuthError';
// import TestAuthError from './pages/TestAuthError'; // Commented out - file doesn't exist
import NotFound from './pages/NotFound';
import TermsOfService from './pages/en/TermsOfService';
import PrivacyPolicy from './pages/en/PrivacyPolicy';
import AuthCallback from './pages/admin/AuthCallback';
import VerifyEmail from './pages/admin/VerifyEmail';
import TestVerifyEmail from './pages/admin/TestVerifyEmail';
import AdminPanel from './pages/AdminPanel';
import AdminRoute from './components/common/AdminRoute';
import PaymentSuccess from './pages/PaymentSuccess';
import JoinCollaborativeKB from './pages/JoinCollaborativeKB';
import CreateNoteTest from './pages/CreateNoteTest';
import TestPurchaseCredits from './pages/TestPurchaseCredits';
import MembershipPage from './pages/MembershipPage';
import SourceDownload from './pages/SourceDownload';
import Guide from './pages/Guide';

// 使用认证存储
import { useAuthStore } from './stores/authStore';
import { setAuthFailureHandler } from './services/api';
import authService from './services/authService';

// 文件重定向组件
const FileRedirect = () => {
  const location = useLocation();
  
  React.useEffect(() => {
    // 获取当前路径
    const currentPath = location.pathname;
    
    // 如果是 /files/* 路径，重定向到后端
    if (currentPath.startsWith('/files/')) {
      const backendUrl = `http://localhost:4000${currentPath}`;
      console.log(`[FileRedirect] 重定向文件请求: ${window.location.origin}${currentPath} -> ${backendUrl}`);
      
      // 直接重定向到后端URL
      window.location.href = backendUrl;
    }
  }, [location]);
  
  return (
    <div style={{ 
      padding: '20px', 
      textAlign: 'center',
      color: '#666'
    }}>
      <p>Redirecting to file server...</p>
      <p>If not redirected automatically, please <a href={`http://localhost:4000${location.pathname}`}>click here</a></p>
    </div>
  );
};

// Stagewise工具栏 - 使用动态导入避免CommonJS兼容性问题
let StagewiseToolbar = null;
let ReactPlugin = null;
let stagewise_loading = false;
let stagewise_loaded = false;
let stagewise_error = null;

  // 添加调试信息
  console.log('当前环境:', process.env.NODE_ENV);
  console.log('是否为开发环境:', process.env.NODE_ENV === 'development');

// 异步加载Stagewise组件
async function loadStagewise() {
  if (stagewise_loading || stagewise_loaded) return;
  
  stagewise_loading = true;
  console.log('开始异步加载Stagewise工具栏...');
  
  try {
    // 使用动态导入
    const [stagewise, reactPlugin] = await Promise.all([
      import('@stagewise/toolbar-react'),
      import('@stagewise-plugins/react')
    ]);
    
    console.log('Stagewise模块:', stagewise);
    console.log('React插件模块:', reactPlugin);
    
    StagewiseToolbar = stagewise.StagewiseToolbar || stagewise.default;
    ReactPlugin = reactPlugin.ReactPlugin || reactPlugin.default;
    
    console.log('StagewiseToolbar:', StagewiseToolbar);
    console.log('ReactPlugin:', ReactPlugin);
    
    if (StagewiseToolbar && ReactPlugin) {
      console.log('✅ Stagewise工具栏和插件动态加载成功!');
      stagewise_loaded = true;
    } else {
      throw new Error('Components loaded incompletely');
    }
  } catch (error) {
    console.error('❌ Stagewise工具栏动态加载失败:', error);
    stagewise_error = error;
  } finally {
    stagewise_loading = false;
  }
}

// 暂时禁用Stagewise工具栏以解决导入问题
// if (process.env.NODE_ENV === 'development') {
//   loadStagewise();
// } else {
//   console.log('Stagewise工具栏仅在开发环境中启用');
// }

// 认证失败处理组件
function AuthFailureHandler() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  
  React.useEffect(() => {
    // 初始化认证服务
    console.log('[AuthFailureHandler] 初始化认证服务');
    
    // 设置认证失败处理函数
    const handleAuthFailure = () => {
      console.log('[AuthFailureHandler] 处理认证失败');
      
      // 显示警告消息
      notification.warning({
        message: 'Authentication Failed',
        description: 'Token has expired or authentication failed. Please log in again.',
        duration: 3,
        placement: 'topRight',
      });
      
      // 使用认证服务处理认证失败
      authService.handleAuthFailure();
      
      // 同时调用原有的 logout 逻辑
      logout();
    };
    
    // 设置处理函数
    setAuthFailureHandler(handleAuthFailure);
    
    return () => {
      // 清理处理函数
      setAuthFailureHandler(null);
    };
  }, [navigate, logout]);
  
  return null; // 这个组件不渲染任何内容
}

function App() {
  const { isAuthenticated } = useAuthStore();
  const { i18n } = useTranslation();
  const [stagewise_ready, setStagewise_ready] = React.useState(false);
  
  // 检查Stagewise加载状态
  React.useEffect(() => {
    console.log('App组件已挂载');
    
    const checkStagewise = () => {
      if (stagewise_loaded && StagewiseToolbar && ReactPlugin) {
        setStagewise_ready(true);
        console.log('Stagewise工具栏已准备就绪');
      } else if (stagewise_error) {
        console.error('Stagewise加载失败:', stagewise_error);
      } else if (!stagewise_loading) {
        // 如果没有在加载但也没有加载成功，重试一次
        if (process.env.NODE_ENV === 'development') {
          console.log('重试加载Stagewise...');
          loadStagewise().then(() => {
            if (stagewise_loaded && StagewiseToolbar && ReactPlugin) {
              setStagewise_ready(true);
            }
          });
        }
      }
    };
    
    checkStagewise();
    
    // 定期检查加载状态
    const interval = setInterval(checkStagewise, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // 添加全局链接点击处理
  React.useEffect(() => {
    const handleGlobalClick = (event) => {
      // 检查是否点击了链接
      const target = event.target.closest('a');
      if (!target) return;
      
      const href = target.getAttribute('href');
      if (!href) return;
      
      // 检查是否是文件链接
      if (href.startsWith('/files/') || href.includes('localhost:3000/files/')) {
        event.preventDefault();
        
        // 构建后端URL
        let backendUrl;
        if (href.startsWith('/files/')) {
          backendUrl = `http://localhost:4000${href}`;
        } else {
          backendUrl = href.replace('localhost:3000', 'localhost:4000');
        }
        
        console.log(`[GlobalClick] 重定向文件链接: ${href} -> ${backendUrl}`);
        
        // 在新标签页打开文件
        window.open(backendUrl, '_blank');
      }
    };
    
    // 添加全局点击监听器
    document.addEventListener('click', handleGlobalClick);
    
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  const antdLocale = i18n.language === 'en' ? enUS : zhCN;

  return (
    <ConfigProvider locale={antdLocale}>
      {/* Stagewise工具栏 - 暂时禁用 */}
      {false && process.env.NODE_ENV === 'development' && (
        <div style={{ position: 'relative', zIndex: 9999 }}>
          {stagewise_ready && StagewiseToolbar && ReactPlugin ? (
            <>
              <div style={{ 
                background: '#f6ffed', 
                padding: '4px 8px', 
                fontSize: '12px', 
                color: '#52c41a',
                borderBottom: '1px solid #b7eb8f'
              }}>
                Stagewise toolbar loaded and ready
              </div>
              <StagewiseToolbar 
                config={{
                  plugins: [ReactPlugin],
                  position: 'top',
                  theme: 'light'
                }}
              />
            </>
          ) : stagewise_loading ? (
            <div style={{ 
              background: '#e6f7ff', 
              padding: '8px', 
              fontSize: '12px', 
              color: '#1890ff',
              borderBottom: '1px solid #91d5ff'
            }}>
              Loading Stagewise toolbar...
            </div>
          ) : stagewise_error ? (
            <div style={{ 
              background: '#fff2f0', 
              padding: '8px', 
              fontSize: '12px', 
              color: '#f5222d',
              borderBottom: '1px solid #ffccc7'
            }}>
              Stagewise toolbar failed to load: {stagewise_error.message}
              <br />
              <small>Please check the console for detailed error information</small>
            </div>
          ) : (
            <div style={{ 
              background: '#fffbe6', 
              padding: '8px', 
              fontSize: '12px', 
              color: '#d46b08',
              borderBottom: '1px solid #ffd591'
            }}>
              Stagewise toolbar could not be loaded
              <br />
              <small>StagewiseToolbar: {StagewiseToolbar ? '✅' : '❌'} | ReactPlugin: {ReactPlugin ? '✅' : '❌'}</small>
            </div>
          )}
        </div>
      )}
      
      <Router>
        <AuthFailureHandler />
        <Routes>
          {/* 文件重定向路由 - 必须放在最前面 */}
          <Route path="/files/*" element={<FileRedirect />} />
          
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth-error" element={<AuthError />} />
          <Route path="/en/terms-of-service" element={<TermsOfService />} />
          <Route path="/en/privacy" element={<PrivacyPolicy />} />
          {/* <Route path="/test-auth-error" element={<TestAuthError />} /> */}
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/join-collaborative-kb/:shareToken" element={<JoinCollaborativeKB />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/source-download" element={<SourceDownload />} />
          <Route path="/guide" element={isAuthenticated ? <Guide /> : <Navigate to="/login" />} />

          {/* 演示页面路由 - 无需认证即可访问 */}
          <Route path="/demo" element={<Dashboard />} />
          <Route path="/create-note-test" element={<CreateNoteTest />} />
          <Route path="/test-purchase-credits" element={<TestPurchaseCredits />} />
          
          {/* 系统管理页面 - 需要管理员权限 */}
          <Route 
            path="/admin/*" 
            element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } 
          />
          
          {/* 独立的Dashboard页面 - 登录成功后的主页 */}
          <Route 
            path="/" 
            element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />} 
          />
          
          {/* 笔记内容独立页面 - 无需导航菜单 */}
          <Route
            path="/note-content"
            element={isAuthenticated ? <NoteContentPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/ai-writer-history"
            element={isAuthenticated ? <AIWriterHistory /> : <Navigate to="/login" />}
          />
          
          {/* 带导航菜单的其他页面 */}
          <Route path="/" element={isAuthenticated ? <AppLayout /> : <Navigate to="/login" />}>
            {/* <Route index element={<Dashboard />} /> */}
            <Route path="notes" element={<Notes />} />
            <Route path="notes/:id" element={<NotePage />} />
            <Route path="create" element={<CreateNote />} />
            <Route path="search" element={<Search />} />
            <Route path="logs" element={<Logs />} />
          </Route>
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
